import logger from '../../../utils/logger';
// rename this to "Send and generate preview email service or something similar. (or break in two)"
/**
 * Generalized Email Service
 * Provides reusable email functionality for different services (offers, settlements, etc.)
 */

import axiosInstance from '../../../fetchConfig/axiosConfig.js';
import { usePersistedDataStore } from '../../store.js';

/**
 * Email template types
 */
export const EMAIL_TEMPLATE_TYPES = {
  OFFERS: 'offers',
  SETTLEMENTS: 'settlements',
  WORKUPS: 'workups',
  OFFERS_AND_WORKUPS: 'offers_and_workups',
  CUSTOM: 'custom'
};

/** Passed to GetNegotiatorsTest: 'Offers' or 'Stips' depending on which flow is invoking. */
export const EMAIL_CONTEXT = { OFFERS: 'Offers', STIPS: 'Stips' };

/**
 * Sanitize data to remove non-JSON elements for email transmission
 * @param {any} data - Data to sanitize
 * @returns {any} Sanitized data
 */
const sanitizeDataForEmail = (data) => {
  if (data === null || data === undefined) return data;
  
  if (typeof data === 'function') return '[Function]';
  
  if (typeof data !== 'object') return data;
  
  if (Array.isArray(data)) {
    return data.map(item => sanitizeDataForEmail(item));
  }
  
  const sanitized = {};
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && typeof value !== 'function') {
      // Skip React components and circular references
      if (value && typeof value === 'object' && value.$$typeof) {
        continue; // Skip React elements
      }
      if (value && typeof value === 'object' && value.constructor && value.constructor.name === 'FiberNode') {
        continue; // Skip React Fiber nodes
      }
      sanitized[key] = sanitizeDataForEmail(value);
    }
  }
  
  return sanitized;
};

/**
 * Generate email preview data for any service type
 * @param {Object} options - Email generation options
 * @param {string} options.templateType - Type of email template to use
 * @param {Array} options.data - Service-specific data (offers, settlements, etc.)
 * @param {string|Object} options.downloadUrl - S3 download URL for attachments (string) OR object with multiple URLs
 * @param {string} options.offers_download_url - Offers download URL (for combined templates)
 * @param {string} options.workups_download_url - Workups download URL (for combined templates)
 * @param {string} options.csvFilename - Filename for attachments
 * @param {Object} options.caseInfo - Case count information
 * @param {Object} options.user - User information
 * @param {string} options.stipulationsUrl - Stipulations URL (optional)
 * @returns {Promise<Object>} Email preview data
 */
export const generateEmailPreview = async (options) => {
  const { 
    templateType, 
    data, 
    downloadUrl, 
    offers_download_url,
    workups_download_url,
    csvFilename, 
    caseInfo, 
    user,
    stipulationsUrl
  } = options;
  
  // Get negotiation object from store and extract needed fields
  const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
  
  const muniCode = getNegotiationObj?.MuniCode;
  const muniString = getNegotiationObj?.muniString;
  const taxYear = getNegotiationObj?.TaxYear;
  const courtDate = getNegotiationObj?.CourtDate === 'All' ? null : getNegotiationObj?.CourtDate;
  const villageRun = getNegotiationObj?.villageRun || 0;
  
  // Sanitize the data to remove non-JSON elements
  const sanitizedData = sanitizeDataForEmail(data);
  
  // Handle downloadUrl - can be a string or an object with multiple URLs
  // If offers_download_url or workups_download_url are provided, use them
  // Otherwise, use the single downloadUrl (backwards compatible)
  let finalDownloadUrl = downloadUrl;
  logger.log('downloadurl', downloadUrl);
  logger.log('offers_download_url', offers_download_url);
  logger.log('workups_download_url', workups_download_url);
  
  if (offers_download_url || workups_download_url) {
    // Create object with multiple URLs for combined templates
    finalDownloadUrl = {
      offers_download_url: offers_download_url || null,
      workups_download_url: workups_download_url || null
    };
    // stipulationsUrl = null; // stupid monkeypatch for now.
  }
  
  const emailContext = templateType === EMAIL_TEMPLATE_TYPES.SETTLEMENTS ? EMAIL_CONTEXT.STIPS : EMAIL_CONTEXT.OFFERS;
  const emailPreviewPayload = {
    templateType,
    emailContext,
    muniCode,
    muniString,
    taxYear,
    courtDate,
    data: sanitizedData,
    villageRun,
    downloadUrl: finalDownloadUrl,
    csvFilename,
    caseInfo,
    user,
    stipulationsUrl
  };

  logger.log('🔍 [EMAIL SERVICE] Generating email preview with payload:', emailPreviewPayload);

  try {
    const response = await axiosInstance.post('/generate_email_preview', emailPreviewPayload);
    
    return response.data;
  } catch (error) {
    logger.error('Error generating email preview:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to generate email preview'
    };
  }
};

/**
 * Send email
 * @param {Object} sendOptions - Email sending options
 * @param {string} sendOptions.templateType - Type of email template to use
 * @param {Array} sendOptions.data - Service-specific data
 * @param {string|Object} sendOptions.fileUrl - Download URL for attachments/files (string) OR object with multiple URLs
 * @param {Array} sendOptions.toEmails - Recipient emails
 * @param {Array} sendOptions.ccEmails - CC emails
 * @param {Array} sendOptions.bccEmails - BCC emails
 * @param {string} sendOptions.subject - Email subject
 * @param {string} sendOptions.emailBody - Email body HTML
 * @param {Object} sendOptions.user - User information
 * @param {Object} sendOptions.caseInfo - Case count information
 * @param {string} sendOptions.senderEmail - Custom sender email address (optional)
 * @param {string} sendOptions.senderName - Custom sender display name (optional)
 * @returns {Promise<Object>} Email sending result
 */
export const sendEmail = async (sendOptions) => {
  const {
    templateType,
    data,
    fileUrl,
    toEmails,
    ccEmails = [],
    bccEmails = [],
    subject,
    emailBody,
    user,
    caseInfo,
    senderEmail,
    senderName,
    xlsDownloadUrl,
    xlsFilename
  } = sendOptions;
  
  // Get negotiation object from store and extract needed fields
  const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
  const muniCode = getNegotiationObj?.MuniCode;
  const muniString = getNegotiationObj?.muniString;
  const taxYear = getNegotiationObj?.TaxYear;
  const villageRun = getNegotiationObj?.villageRun || 0;
  
  // Sanitize the data to remove non-JSON elements
  const sanitizedData = sanitizeDataForEmail(data);
  
  // fileUrl can be a string (single URL) or object (multiple URLs)
  // Pass it as-is to the backend
  
  // Structure payload object for backend
  const sendEmailPayload = {
    templateType,
    muniCode,
    taxYear,
    data: sanitizedData,
    villageRun,
    downloadUrl: fileUrl,
    toEmails,
    ccEmails,
    bccEmails,
    subject,
    emailBody,
    user,
    muniString,
    caseInfo,
    senderEmail,
    senderName,
    xlsDownloadUrl,
    xlsFilename
  };

  logger.log('📧 [EMAIL SERVICE] Sending email with payload:', sendEmailPayload);
  
  try {
    const response = await axiosInstance.post('/send_email', sendEmailPayload);
    
    return response.data;
  } catch (error) {
    logger.error('Error sending email:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to send email'
    };
  }
};